import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, FileText, ArrowRight, Presentation, PieChart, BarChart3, Table2, Paperclip, X, ArrowUp, Loader2, HardDrive, ChevronDown, Check, Palette, LayoutTemplate, Eye, SlidersHorizontal, Briefcase, Boxes } from 'lucide-react';
import DriveFilePicker from './DriveFilePicker';
import TemplateSlidesModal from './TemplateSlidesModal';
import NativeLayoutPickerModal from './NativeLayoutPickerModal';
import HubSpotDealPickerModal from './HubSpotDealPickerModal';
import OdooRecordPickerModal from './OdooRecordPickerModal';
import { checkConnection } from '../services/driveService';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { toast } from 'sonner';
import { withBackend, toThumbnailUrl } from '../utils/api';
import { motion } from 'framer-motion';
import { TEMPLATES } from './TemplateGallery';
import ModelSelect from './ModelSelect';
import { getDefaultModel } from '../utils/models';
import { SHOW_SHEETS, SHOW_PPTX_TEMPLATES } from '../utils/features';

const MODES = [
  { id: 'presentation', label: 'Presentation', icon: Presentation, desc: 'Slide decks for pitches, reports & trainings' },
  { id: 'proposal', label: 'Proposal', icon: Briefcase, desc: 'Client proposals & RFP responses — facts only, never invented' },
  { id: 'infographic', label: 'Infographic', icon: PieChart, desc: 'Visual data stories on a single page' },
  { id: 'document', label: 'Document', icon: FileText, desc: 'Reports, proposals & multi-page documents' },
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3, desc: 'KPI dashboards with charts & metrics' },
  { id: 'sheets', label: 'Sheets', icon: Table2, desc: 'Smart spreadsheets & structured data' },
  { id: 'consultant', label: 'AI Consultant', icon: Sparkles, desc: 'Let AI decide the best format for your idea' },
];

export function Dashboard({ userName, projects = [], isLoading = false, onProjectSelect, onDeleteProject, onStartPresentation, onStartProposal, onStartInfographic, onStartDocument, onStartDashboard, onStartSheets, onStartPlanning, userId, usageData }) {
  const [selectedMode, setSelectedMode] = useState('presentation');
  // Brief leave animation before handing off to the workspace, so submit feels
  // like a transition instead of a hard page swap.
  const [leaving, setLeaving] = useState(false);
  const [promptText, setPromptText] = useState('');
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [showDrivePicker, setShowDrivePicker] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  // Enter/leave fire for every child the cursor crosses; a depth counter is the
  // only reliable way to know when the drag actually left the box.
  const dragDepth = useRef(0);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  // null = follow the product default; the pill shows the resolved model
  // (a hardcoded 'opus' here is why the bar ignored the Kimi default)
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState('none');
  const [brands, setBrands] = useState([]);
  // Saved templates: HTML deck templates + native PPT masters, merged into
  // one row under the prompt. Click selects; kind decides the engine.
  const [deckTemplates, setDeckTemplates] = useState([]);
  const [selectedDeckTemplate, setSelectedDeckTemplate] = useState(null);
  const [viewTemplate, setViewTemplate] = useState(null); // deck template being browsed
  const [layoutPickerTemplate, setLayoutPickerTemplate] = useState(null); // native template whose layout allowlist is being edited
  const [hubspotConnected, setHubspotConnected] = useState(false);
  const [showHubSpotPicker, setShowHubSpotPicker] = useState(false);
  const [odooConnected, setOdooConnected] = useState(false);
  const [showOdooPicker, setShowOdooPicker] = useState(false);

  // Show the connector buttons only for connected accounts (checked once).
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    checkConnection(userId, 'hubspot')
      .then(s => { if (!cancelled) setHubspotConnected(!!s.connected); })
      .catch(() => { /* not connected / not configured */ });
    checkConnection(userId, 'odoo')
      .then(s => { if (!cancelled) setOdooConnected(!!s.connected); })
      .catch(() => { /* not connected */ });
    return () => { cancelled = true; };
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const [deckResp, nativeResp] = await Promise.all([
          fetch(withBackend(`/api/deck-templates?user_id=${encodeURIComponent(userId)}`)),
          fetch(withBackend(`/api/native-templates?user_id=${encodeURIComponent(userId)}`)),
        ]);
        if (cancelled) return;
        const merged = [];
        if (deckResp.ok) {
          const d = await deckResp.json().catch(() => ({}));
          (d.templates || []).forEach(t => merged.push({ ...t, kind: 'deck' }));
        }
        if (SHOW_PPTX_TEMPLATES && nativeResp.ok) {
          const d = await nativeResp.json().catch(() => ({}));
          (d.templates || []).forEach(t => merged.push({
            kind: 'native',
            id: t.template_id,
            template_id: t.template_id,
            name: t.name,
            thumbnail_url: t.thumbnail_url,
            slide_count: (t.layouts || []).filter(l => (l.placeholders || []).length > 0).length,
            layouts: t.layouts || [],
            disabled_layouts: t.disabled_layouts || [],
          }));
        }
        setDeckTemplates(merged);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 160) + 'px';
    }
  }, [promptText]);

  // Load brand identities for the inline design-system picker.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(withBackend(`/api/brand-identities?user_id=${userId}`));
        if (resp.ok && !cancelled) {
          const data = await resp.json();
          setBrands(data.brand_identities || []);
          // Make the picker truthful: an auto-apply default brand gets applied
          // by the workspace anyway, so show it instead of "Auto". Functional
          // update avoids clobbering a pick the user made mid-fetch.
          const auto = (data.brand_identities || []).find(b => b.is_default && b.auto_apply);
          if (auto) setSelectedBrand(prev => (prev === 'none' ? auto.id : prev));
        }
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  if (isLoading) {
    return (
      <div className="p-8 min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground text-sm">Loading...</div>
      </div>
    );
  }

  const handleDriveFileSelected = (content) => {
    setShowDrivePicker(false);
    const fileName = content.file_name || 'drive-file.txt';

    // For spreadsheets with pre-parsed excel_data, create a proper File
    // with the structured data attached so downstream XLSX parsing can be skipped.
    if (content.excel_data && Array.isArray(content.excel_data) && content.excel_data.length > 0) {
      // Create a placeholder file with .xlsx extension so the file-type checks pass
      const blob = new Blob([''], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const file = new File([blob], fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`, { type: blob.type });
      file._driveContent = content;
      // Pre-parsed structured data — matches the format from /api/google-sheets/fetch
      file._parsedExcelData = content.excel_data;
      // Source info for live refresh (equivalent to Google Sheet URL)
      file._driveSourceInfo = content.source_info || null;
      setAttachedFiles(prev => [...prev, file]);
      const totalRows = content.excel_data.reduce((sum, s) => sum + (s.total_rows || s.rows?.length || 0), 0);
      toast.success(`Parsed ${fileName}: ${content.excel_data.map(s => `${s.sheet_name} (${s.total_rows || s.rows?.length || 0} rows)`).join(', ')}`);
    } else {
      // Text-based files (PDF, DOCX, TXT)
      const text = content.extracted_text || '';
      const blob = new Blob([text], { type: 'text/plain' });
      const file = new File([blob], fileName, { type: 'text/plain' });
      file._driveContent = content;
      file._driveSourceInfo = content.source_info || null;
      setAttachedFiles(prev => [...prev, file]);
      toast.success(`Loaded: ${fileName}`);
    }
  };

  // Analyze a file the moment it's attached (not after the prompt is sent), so
  // the extracted content is ready by the time the user submits and the LLM gets
  // file + prompt together. The result is stashed on the File object; the
  // workspace's processSingleFile reads file._preExtracted and skips re-fetching.
  const preExtractFile = async (file) => {
    const name = file.name || '';
    // Spreadsheets parse client-side in the workspace; txt/md are read as
    // plain text client-side too (sending them here just earned a 400 from
    // the extractor); drive/pre-parsed files already carry content.
    if (/\.(xlsx|xls|csv|txt|md)$/i.test(name) || file._driveContent || file._parsedExcelData || file._preExtracted) return;
    const isImage = /\.(png|jpe?g|webp)$/i.test(name);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (!isImage) {
        formData.append('structure', 'false');
        formData.append('full_text', 'true');
        formData.append('fast', 'true');
        formData.append('extract_images', 'true');
      }
      const fetchP = fetch(withBackend('/api/content/extract'), { method: 'POST', body: formData });
      let base64 = null;
      if (isImage) {
        base64 = await new Promise((resolve) => {
          const r = new FileReader();
          r.onload = (ev) => resolve(ev.target.result);
          r.onerror = () => resolve(null);
          r.readAsDataURL(file);
        });
      }
      const resp = await fetchP;
      if (resp.ok) {
        file._preExtracted = await resp.json();
        if (isImage && base64) file._preBase64 = base64;
      }
    } catch (_) { /* best-effort: the workspace re-extracts if this didn't land */ }
  };

  // Shared ingestion for the file input and drag-and-drop: attach each file and
  // start analyzing it immediately. Submit stays disabled (isFileLoading) until
  // analysis finishes, so the LLM always receives file content with the prompt.
  const addFiles = (files) => {
    if (!files.length) return;
    setIsFileLoading(true);
    let pending = files.length;
    const finish = () => { pending -= 1; if (pending === 0) setIsFileLoading(false); };
    files.forEach((f) => {
      setAttachedFiles(prev => [...prev, f]);
      preExtractFile(f).finally(finish);
    });
  };

  const ACCEPTED_FILE_RE = /\.(txt|md|docx|xlsx|xls|csv|pdf|png|jpe?g|webp|pptx)$/i;

  const handleDrop = (e) => {
    e.preventDefault();
    dragDepth.current = 0;
    setDragActive(false);
    const dropped = Array.from(e.dataTransfer?.files || []);
    if (!dropped.length) return;
    const accepted = dropped.filter(f => ACCEPTED_FILE_RE.test(f.name || ''));
    const skipped = dropped.length - accepted.length;
    if (skipped > 0) toast.error(`${skipped} unsupported file${skipped > 1 ? 's' : ''} skipped`);
    addFiles(accepted);
  };

  const leaveThen = (fn) => {
    setLeaving(true);
    setTimeout(fn, 180);
  };

  const handleSubmit = () => {
    if (isFileLoading || leaving) return;
    if (!promptText.trim() && attachedFiles.length === 0) return;
    const handlers = {
      consultant: onStartPlanning,
      presentation: onStartPresentation,
      proposal: onStartProposal,
      infographic: onStartInfographic,
      document: onStartDocument,
      dashboard: onStartDashboard,
      sheets: onStartSheets,
    };
    // Pass the full array of attached files (the workspace ingests them all).
    leaveThen(() => handlers[selectedMode]?.(promptText.trim(), attachedFiles, buildInlineAnswers()));
  };

  // Pre-seed the flow with the inline control choices so the questionnaire
  // doesn't re-ask them. "Auto" brand deliberately leaves brandIdentity unset:
  // a prompt-named brand ("use my Prezlab brand") fills it via preference
  // extraction, and otherwise the questionnaire asks — the intended behavior.
  const buildInlineAnswers = () => {
    // Resolve "auto" here so the questionnaire sees a concrete model and
    // doesn't re-ask; the pill and the request stay in agreement.
    const ia = { htmlModel: selectedModel || getDefaultModel() };
    if (selectedBrand && selectedBrand !== 'none') ia.brandIdentity = selectedBrand;
    if (selectedDeckTemplate?.kind === 'deck' && selectedMode === 'presentation') {
      ia.deckTemplateId = selectedDeckTemplate.id;
      // Variations generate untemplated samples that would interleave with
      // template-guided slides — a template IS the design decision.
      ia.wantVariations = 'no';
      // The template also decides the visual identity, so don't ask about
      // brand — unless the user explicitly picked one alongside (above).
      if (!ia.brandIdentity) ia.brandIdentity = 'none';
    }
    if (selectedDeckTemplate?.kind === 'native' && selectedMode === 'presentation') {
      // Native masters ride the standard flow (questions -> outline) but
      // generate through their own engine; design questions are moot since
      // the master IS the design.
      ia.nativeTemplateId = selectedDeckTemplate.id;
      ia.wantVariations = 'no';
      ia.brandIdentity = 'none';
      ia.runQa = 'yes';
      // Native fill is text-only (no image generation) — also arms the
      // client-side duplicate gate so the imagery question never shows.
      ia.imageryMode = 'none';
    }
    return ia;
  };

  const placeholders = {
    consultant: 'Type your message here...',
    presentation: 'Describe your presentation topic or paste content...',
    proposal: 'Paste the RFP or describe the engagement you are proposing for...',
    infographic: 'What story should your infographic tell?',
    document: 'What document would you like to create?',
    dashboard: 'Describe your dashboard or paste a Google Sheets link...',
    sheets: 'Describe the spreadsheet you need...',
  };

  // Flex column + justify-center floats the greeting/prompt block to the
  // vertical middle of the viewport (ChatGPT-style) instead of pinning it to
  // the top; pb-[10vh] biases it slightly above true center, which reads as
  // optically centered. Grows + scrolls normally when examples expand.
  return (
    <motion.div
      animate={leaving ? { opacity: 0, y: -14 } : { opacity: 1, y: 0 }}
      transition={{ duration: 0.18, ease: 'easeIn' }}
      className="min-h-screen max-w-6xl mx-auto px-6 py-10 pb-[10vh] flex flex-col justify-center"
    >
      {/* ─── Prompt-First Creation UI ─── */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-10"
      >
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-1">
          <span className="text-foreground">Hello, </span>
          <span className="text-accent inline-block max-w-[60vw] truncate align-bottom">
            {(userName || 'there').split(' ')[0]}
          </span>
        </h1>
        <p className="text-lg sm:text-xl md:text-3xl font-semibold text-muted-foreground/70">
          How can I help you today?
        </p>
      </motion.div>

      {/* ─── Mode tabs (above the prompt box) ─── */}
      <div className="max-w-3xl mx-auto mb-4 flex flex-wrap items-center justify-center gap-1.5">
        {MODES.filter(m => m.id !== 'consultant' && (SHOW_SHEETS || m.id !== 'sheets')).map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => { setSelectedMode(id); textareaRef.current?.focus(); }}
            className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
              selectedMode === id
                ? 'bg-accent/10 text-accent ring-1 ring-accent/30'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* ─── Wizardly-style Prompt Box ─── */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="w-full max-w-4xl mx-auto mb-8"
      >
        <div
          onDragEnter={(e) => {
            e.preventDefault();
            dragDepth.current += 1;
            if (e.dataTransfer?.types?.includes('Files')) setDragActive(true);
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={(e) => {
            e.preventDefault();
            dragDepth.current = Math.max(0, dragDepth.current - 1);
            if (dragDepth.current === 0) setDragActive(false);
          }}
          onDrop={handleDrop}
          className={`relative rounded-2xl border bg-card shadow-prompt focus-within:shadow-prompt-focus transition-all duration-300 overflow-hidden ${
            dragActive ? 'border-accent/60 bg-accent/5' : 'border-border focus-within:border-accent/30'
          }`}
        >
          {/* Drop overlay */}
          {dragActive && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-accent/5 backdrop-blur-[1px] pointer-events-none">
              <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-card px-4 py-2 text-sm font-medium text-accent shadow-md">
                <Paperclip className="w-4 h-4" />
                Drop files to attach
              </span>
            </div>
          )}
          {/* Attached file preview */}
          {(attachedFiles.length > 0 || isFileLoading) && (
            <div className="px-4 pt-3 flex flex-wrap gap-2">
              {attachedFiles.map((f, i) => (
                <div key={i} className="inline-flex items-center gap-2 bg-accent/10 text-accent px-3 py-1.5 rounded-lg text-xs font-medium">
                  <Paperclip className="w-3.5 h-3.5" />
                  <span className="truncate max-w-[200px]">{f.name}</span>
                  <button onClick={() => setAttachedFiles(prev => prev.filter((_, j) => j !== i))} className="hover:text-destructive transition-colors">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {isFileLoading && (
                <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-3 py-1.5 rounded-lg text-xs font-medium">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Analyzing file...</span>
                </div>
              )}
            </div>
          )}

          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={promptText}
            onChange={(e) => setPromptText(e.target.value)}
            onPaste={(e) => {
              // r171: pasted images attach like dropped files; text pastes
              // are untouched (no preventDefault unless an image was taken).
              const imgs = Array.from(e.clipboardData?.items || [])
                .filter(it => it.type && it.type.startsWith('image/'))
                .map(it => it.getAsFile())
                .filter(Boolean);
              if (imgs.length) {
                e.preventDefault();
                addFiles(imgs.map((f, i) => (f.name ? f : new File([f], `pasted-image-${i + 1}.png`, { type: f.type }))));
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !isFileLoading && (promptText.trim() || attachedFiles.length > 0)) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder={placeholders[selectedMode]}
            autoFocus
            rows={2}
            className="w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground/70 px-4 pt-4 pb-2 resize-none focus:outline-none min-h-[64px] max-h-[160px]"
          />

          {/* Bottom toolbar */}
          <div className="flex items-center justify-between px-3 pb-3">
            <div className="flex items-center gap-1">
              {/* File attachment */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".txt,.md,.docx,.xlsx,.xls,.csv,.pdf,.png,.jpg,.jpeg,.webp,.pptx"
                onChange={(e) => {
                  addFiles(Array.from(e.target.files || []));
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-8 h-8 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground flex items-center justify-center transition-colors"
                title="Attach file"
              >
                <Paperclip className="w-4 h-4" />
              </button>
              {userId && (
                <button
                  onClick={() => setShowDrivePicker(true)}
                  className="w-8 h-8 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground flex items-center justify-center transition-colors"
                  title="Import from Google Drive or OneDrive"
                >
                  <HardDrive className="w-4 h-4" />
                </button>
              )}
              {hubspotConnected && (
                <button
                  onClick={() => setShowHubSpotPicker(true)}
                  className="w-8 h-8 rounded-lg hover:bg-muted text-muted-foreground hover:text-orange-600 flex items-center justify-center transition-colors"
                  title="Create from a HubSpot deal"
                >
                  <Briefcase className="w-4 h-4" />
                </button>
              )}
              {odooConnected && (
                <button
                  onClick={() => setShowOdooPicker(true)}
                  className="w-8 h-8 rounded-lg hover:bg-muted text-muted-foreground hover:text-purple-600 flex items-center justify-center transition-colors"
                  title="Create from an Odoo opportunity or project"
                >
                  <Boxes className="w-4 h-4" />
                </button>
              )}

              {/* Inline controls: design system + model (pre-seed the flow) */}
              <div className="w-px h-5 bg-border mx-1" />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors max-w-[180px]"
                    title="Design system / brand"
                  >
                    <Palette className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">
                      {selectedDeckTemplate && selectedMode === 'presentation'
                        ? `${selectedDeckTemplate.kind === 'native' ? 'PPT template' : 'Template'}: ${selectedDeckTemplate.name}${selectedBrand !== 'none' && selectedDeckTemplate.kind === 'deck' ? ` · ${brands.find(b => b.id === selectedBrand)?.name || 'Brand'}` : ''}`
                        : selectedBrand === 'none'
                          ? 'Auto design'
                          : (brands.find(b => b.id === selectedBrand)?.name || 'Design system')}
                    </span>
                    <ChevronDown className="w-3 h-3 flex-shrink-0 opacity-60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-60">
                  {selectedDeckTemplate && selectedMode === 'presentation' && (
                    <DropdownMenuItem onClick={() => setSelectedDeckTemplate(null)}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm">Clear template</p>
                        <p className="text-[11px] text-muted-foreground truncate">Using "{selectedDeckTemplate.name}"</p>
                      </div>
                      <X className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => setSelectedBrand('none')}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm">Auto</p>
                      <p className="text-[11px] text-muted-foreground">Let the AI pick a design system</p>
                    </div>
                    {selectedBrand === 'none' && <Check className="w-4 h-4 text-accent flex-shrink-0" />}
                  </DropdownMenuItem>
                  {brands.map(b => (
                    <DropdownMenuItem key={b.id} onClick={() => setSelectedBrand(b.id)}>
                      <span className="flex-1 truncate text-sm">{b.name}</span>
                      {selectedBrand === b.id && <Check className="w-4 h-4 text-accent flex-shrink-0" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <ModelSelect variant="toolbar" value={selectedModel} onChange={setSelectedModel} />
            </div>

            {/* Send button */}
            <button
              onClick={handleSubmit}
              disabled={isFileLoading || (!promptText.trim() && attachedFiles.length === 0)}
              className="w-8 h-8 rounded-full bg-foreground text-background flex items-center justify-center hover:opacity-80 transition-all disabled:opacity-25 disabled:cursor-not-allowed"
            >
              <ArrowUp className="w-4 h-4" />
            </button>
          </div>
        </div>

      </motion.div>

      {/* ─── First-run hint (only until the first project exists) ─── */}
      {projects.length === 0 && (
        <div className="max-w-3xl mx-auto mb-4 w-full rounded-xl border border-accent/20 bg-accent/[0.04] px-4 py-3 flex flex-wrap items-center justify-center gap-x-6 gap-y-1 text-xs text-muted-foreground">
          <span className="text-sm font-semibold text-foreground">Create your first presentation</span>
          <span><span className="font-semibold text-accent">1</span> Describe your idea</span>
          <span><span className="font-semibold text-accent">2</span> Answer a few quick questions</span>
          <span><span className="font-semibold text-accent">3</span> Get a designed, on-brand deck</span>
        </div>
      )}

      {/* ─── My templates (Presentation tab) + example chips ─── */}
      <motion.div
        key={selectedMode}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.18, ease: 'easeOut' }}
        className="w-full"
      >
          {/* Prompt-starter chips first, templates below */}
          <div className="max-w-3xl mx-auto mt-4 flex flex-wrap items-center justify-center gap-2 w-full">
            {(TEMPLATES[selectedMode === 'consultant' ? 'presentation' : selectedMode] || []).slice(0, 4).map(t => (
              <button
                key={t.id}
                onClick={() => { setPromptText(t.desc); textareaRef.current?.focus(); }}
                title={t.desc}
                className="px-4 py-1.5 rounded-full border border-border/70 bg-card text-sm text-muted-foreground whitespace-nowrap hover:text-foreground hover:border-accent/40 hover:bg-accent/[0.06] hover:shadow-md transition-all duration-200"
              >
                {t.name}
              </button>
            ))}
          </div>

          {selectedMode === 'presentation' && deckTemplates.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
              className="max-w-4xl mx-auto mt-4 w-full"
            >
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2 text-center">
                My templates
              </p>
              <div className="flex flex-wrap items-start justify-center gap-4">
                {deckTemplates.slice(0, 6).map(t => {
                  const selected = selectedDeckTemplate?.id === t.id && selectedDeckTemplate?.kind === t.kind;
                  return (
                    <div
                      key={`${t.kind}-${t.id}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedDeckTemplate(prev => (prev?.id === t.id && prev?.kind === t.kind ? null : t))}
                      onKeyDown={(e) => {
                        // Only when the CARD itself is focused — Enter on the
                        // nested Eye button must not also toggle selection.
                        if (e.key === 'Enter' && e.target === e.currentTarget) {
                          setSelectedDeckTemplate(prev => (prev?.id === t.id ? null : t));
                        }
                      }}
                      title={selected ? `${t.name} — click to deselect` : `Generate in the style of "${t.name}"`}
                      className={`group relative w-[196px] cursor-pointer text-left rounded-xl border overflow-hidden bg-card transition-all duration-200 ${
                        selected
                          ? 'border-accent ring-2 ring-accent/30 shadow-md'
                          : 'border-border/70 hover:border-accent/40 hover:shadow-md'
                      }`}
                    >
                      <div className="aspect-video bg-muted flex items-center justify-center overflow-hidden">
                        {t.thumbnail_url ? (
                          <img src={toThumbnailUrl(t.thumbnail_url)} alt="" className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                          <LayoutTemplate className="w-6 h-6 text-muted-foreground" />
                        )}
                      </div>
                      {t.kind === 'native' && (
                        <>
                          <span className="absolute top-1.5 left-1.5 rounded bg-amber-500/90 text-white text-[9px] font-bold px-1.5 py-0.5 tracking-wide">
                            PPTX
                          </span>
                          <button
                            onClick={(e) => { e.stopPropagation(); setLayoutPickerTemplate(t); }}
                            title="Choose which layouts the AI may use"
                            className="absolute top-1.5 right-1.5 w-7 h-7 rounded-lg bg-card/90 border border-border text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-accent hover:border-accent/40 flex items-center justify-center transition-all"
                          >
                            <SlidersHorizontal className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                      {t.kind === 'deck' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setViewTemplate(t); }}
                          title="View slides"
                          className="absolute top-1.5 right-1.5 w-7 h-7 rounded-lg bg-card/90 border border-border text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-accent hover:border-accent/40 flex items-center justify-center transition-all"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <div className="px-2.5 py-2">
                        <p className="text-xs font-medium text-foreground truncate">{t.name}</p>
                        <p className={`text-[11px] ${selected ? 'text-accent font-medium' : 'text-muted-foreground'}`}>
                          {selected
                            ? 'Selected ✓'
                            : t.kind === 'native'
                              ? `${t.slide_count} layouts · native PPT`
                              : `${t.slide_count} slides`}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}
      </motion.div>

      {/* ─── AI Consultant banner ─── */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.15 }}
        className="max-w-3xl mx-auto mt-5 w-full"
      >
        <button
          onClick={() => leaveThen(() => onStartPlanning?.(promptText.trim(), attachedFiles, buildInlineAnswers()))}
          className="w-full flex items-center gap-3 rounded-xl border border-border/60 bg-card px-4 py-3 text-left hover:border-accent/40 hover:bg-accent/[0.03] transition-all group"
        >
          <div className="w-9 h-9 rounded-lg bg-accent/10 text-accent flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4.5 h-4.5" />
          </div>
          <div className="min-w-0 flex-1">
            <span className="text-sm font-semibold text-foreground group-hover:text-accent transition-colors">Not sure which format?</span>
            <span className="text-sm text-muted-foreground"> Ask the AI Consultant — it plans the right deliverable for your idea.</span>
          </div>
          <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-accent group-hover:translate-x-0.5 transition-all flex-shrink-0" />
        </button>
      </motion.div>

      {/* Drive File Picker Modal */}
      {showDrivePicker && userId && (
        <DriveFilePicker
          userId={userId}
          onFileSelected={handleDriveFileSelected}
          onClose={() => setShowDrivePicker(false)}
        />
      )}

      {/* Template slide browser */}
      {viewTemplate && userId && (
        <TemplateSlidesModal
          templateId={viewTemplate.id}
          templateName={viewTemplate.name}
          userId={userId}
          onClose={() => setViewTemplate(null)}
        />
      )}
      {showHubSpotPicker && (
        <HubSpotDealPickerModal
          userId={userId}
          onClose={() => setShowHubSpotPicker(false)}
          onPicked={(contextText, deal) => {
            // The deal context becomes part of the prompt — the standard
            // flow (classifier, clarifier, outline) takes it from there.
            setPromptText(prev => {
              const base = prev.trim()
                ? prev.trim()
                : `Create a proposal presentation for ${deal?.company || deal?.name || 'this prospect'}.`;
              return `${base}\n\n${contextText}`;
            });
            toast.success(`Deal context added${deal?.name ? `: ${deal.name}` : ''} — describe anything extra, then generate.`);
          }}
        />
      )}
      {showOdooPicker && (
        <OdooRecordPickerModal
          userId={userId}
          onClose={() => setShowOdooPicker(false)}
          onPicked={(contextText, record) => {
            // Same pattern as HubSpot: the record context becomes part of the
            // prompt and the standard flow takes it from there.
            setPromptText(prev => {
              const base = prev.trim()
                ? prev.trim()
                : record?.kind === 'project'
                  ? `Create a project status report for ${record?.name || 'this project'}.`
                  : `Create a proposal presentation for ${record?.subtitle || record?.name || 'this prospect'}.`;
              return `${base}\n\n${contextText}`;
            });
            toast.success(`Odoo context added${record?.name ? `: ${record.name}` : ''} — describe anything extra, then generate.`);
          }}
        />
      )}
      {layoutPickerTemplate && (
        <NativeLayoutPickerModal
          template={layoutPickerTemplate}
          userId={userId}
          onClose={() => setLayoutPickerTemplate(null)}
          onSaved={(templateId, disabledLayouts) => {
            setDeckTemplates(prev => prev.map(t =>
              t.kind === 'native' && t.id === templateId
                ? { ...t, disabled_layouts: disabledLayouts }
                : t
            ));
          }}
        />
      )}

    </motion.div>
  );
}
